<template>
  <ChatLayout>
    <template #header>
      <div class="overline d-none">
        <v-skeleton-loader
          v-if="loadingEngines"
          type="heading"
          width="480"
          class="d-none d-md-block skeleton-pulse"
        />
        <v-select
          v-else
          class="d-none d-md-block"
          :items="engines"
          item-text="name"
          item-value="id"
          v-model="chatEngine"
          hide-details
          dense
        ></v-select>
      </div>
    </template>

    <template #content>
      <div v-if="initRouting" class="routing-chips pa-3 d-flex align-center">
        <v-chip small color="primary" outlined class="mr-2">
          <v-icon left x-small>mdi-domain</v-icon>
          {{ sectorLabel }}
        </v-chip>
        <v-chip v-if="initRouting.stage2" small color="secondary" outlined>
          <v-icon left x-small>mdi-tag-outline</v-icon>
          {{ modeLabel }}
        </v-chip>
      </div>
      <div
        class="messages overflow-y-auto pa-md-4 pa-2"
        ref="messagesContainer"
        :class="{ 'scrolled': isScrolled }"
        v-if="hasMessages"
      >
        <v-btn
          @click="animateScrollToTop()"
          fab
          fixed
          absolute
          right
          bottom
          v-if="isScrolled"
          elevation="1"
        >
          <v-icon>mdi-arrow-up</v-icon>
        </v-btn>
        <div v-for="m in messages" :key="m.id" class="message pa-md-3" :class="m.role">
          <div>
            <MessageBubble :message="m" />
          </div>
        </div>
        <div v-if="thinking" class="bubble thinking-bubble pa-3 bubble d-flex align-center">
          <div class="thinking">
            <span class="dot"></span>
            <span class="dot"></span>
            <span class="dot"></span>
          </div>
          <div class="ml-2">
            Je réfléchis...
          </div>
        </div>
      </div>
      <div v-if="!thinking && !isStreaming" class="chat-input pa-md-4 d-flex align-center flex-grow-1" :class="hasMessages ? '' : 'no-messages'">
        <ChatInput @send="send" :disabled="sending" />
      </div>
    </template>
  </ChatLayout>
</template>

<script>
import ChatLayout from '@/layout/ChatLayout.vue'
import MessageBubble from '@/components/chat/MessageBubble.vue'
import ChatInput from '@/components/chat/ChatInput.vue'
import { fetchEngines } from '@/services/llmApi'

export default {
  name: 'ChatView',
  components: { ChatLayout, MessageBubble, ChatInput },
  data() {
    return {
      sending: false,
      loadingEngines: true,
      engines: [],
      chatEngine: null,
      isScrolled: false,
    }
  },
  computed: {
    thinking() {
      return this.$store.state.chat.thinking
    },
    currentConversationId() {
      return this.$route.params.id || null
    },
    messages() {
      return this.$store.state.chat.messages
    },
    lastMessage() {
      if (this.messages && this.messages.length > 0) {
        return this.messages[this.messages.length - 1]
      }
      return null
    },
    hasMessages() {
      return this.messages && this.messages.length > 0
    },
    initRouting() {
      return this.$store.state.chat.initRouting
    },
    sectorLabel() {
      const labels = { isolation: 'Isolation & Plâtre', sols: 'Carrelage & Sols', other: 'Autre' }
      return labels[this.initRouting?.stage1] || this.initRouting?.stage1 || ''
    },
    modeLabel() {
      const labels = { quantitatif: 'Devis quantitatif', discussion: 'Discussion' }
      return labels[this.initRouting?.stage2] || this.initRouting?.stage2 || ''
    },
    isStreaming() {
      const last = this.messages[this.messages.length - 1]
      return last?.streaming === true
    },
  },
  watch: {
    currentConversationId(newId) {
      this.$store.commit('chat/CLEAR_INIT_ROUTING')
      if (newId && newId !== this.$store.state.chat.currentConversationId) {
        this.$store.dispatch('chat/loadConversation', newId)
      } else if (!newId) {
        this.$store.dispatch('chat/newConversation')
      }
      this.$nextTick(() => {
        setTimeout(() => {
          this.setupScrollListener()
          this.animateScrollToBottom(1000)
        }, 100)
      })
    },
    messages() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.setupScrollListener()
          this.animateScrollToBottom(1000)
        }, 100)
      })
    },
    lastMessage: {
      handler() {
        this.animateScrollToBottom(10)
      },
      deep: true,
    },
  },
  methods: {
    async send(text) {
      if (!text) return
      this.$store.dispatch('chat/sendMessage', { text, engineId: this.chatEngine })
    },
    animateScrollToBottom(duration = 1000) {
      const el = this.$refs.messagesContainer
      if (el) {
        this.isScrolled = true
        this.$vuetify.goTo(el.scrollHeight, { container: el, duration })
      }
    },
    animateScrollToTop(duration = 1000) {
      const el = this.$refs.messagesContainer
      if (el) {
        this.$vuetify.goTo(0, { container: el, duration })
        this.isScrolled = false
      }
    },
    handleScroll() {
      const el = this.$refs.messagesContainer
      if (el) {
        this.isScrolled = el.scrollTop > 0
      }
    },
    setupScrollListener() {
      const el = this.$refs.messagesContainer
      if (el) {
        el.addEventListener('scroll', this.handleScroll)
      }
    },
    removeScrollListener() {
      const el = this.$refs.messagesContainer
      if (el) {
        el.removeEventListener('scroll', this.handleScroll)
      }
    },
  },
  async mounted() {
    fetchEngines().then(data => {
      this.engines = data
      if (data.length) this.chatEngine = data[0].id
    }).catch(() => {
      this.$store.commit('chat/SHOW_NOTIFICATION', { type: 'error', message: "Impossible de charger les moteurs IA." })
    }).finally(() => {
      this.loadingEngines = false
    })
    if (this.currentConversationId) {
      await this.$store.dispatch('chat/loadConversation', this.currentConversationId)
      if (this.$store.state.chat.initRouting) {
        this.$store.dispatch('chat/sendAgentInitReply')
        this.$store.commit('chat/CLEAR_INIT_ROUTING')
      }
      this.$nextTick(() => {
        setTimeout(() => {
          this.setupScrollListener()
          this.animateScrollToBottom(1000)
        }, 200)
      })
    }
  },
  beforeDestroy() {
    this.removeScrollListener()
  },
}
</script>

<style lang="scss" scoped>
.skeleton-pulse ::v-deep .v-skeleton-loader__bone {
  animation: skeleton-shimmer 1.2s ease-in-out infinite;
  background: linear-gradient(90deg, #e0e0e0 25%, #bdbdbd 50%, #e0e0e0 75%);
  background-size: 200% 100%;
}

@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.v-btn--fab.v-size--default.v-btn--absolute.v-btn--bottom {
  bottom: 16px;
}

.chat-body {
  overflow-y: auto;
}

.thinking-bubble {
  background: var(--app-assistant-bubble-bg, #F3F6FB);
  color: var(--app-assistant-bubble-text, #111);
  border-radius: 12px;
  max-width: 80%;
  position: relative;
  overflow: hidden;
}

.thinking-bubble::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(25, 118, 210, 0.1);
  transform: translate(-50%, -50%);
  animation: ripple 2s infinite;
}

@keyframes ripple {
  0% { width: 0; height: 0; opacity: 1; }
  100% { width: 200px; height: 200px; opacity: 0; }
}

.routing-chips {
  border-bottom: 1px solid rgba(0,0,0,0.06);
  background: rgba(0,0,0,0.01);
}

.chat-header { border-bottom: 1px solid rgba(0,0,0,0.06); }

.chat-input {
  border-top: 1px solid rgba(0,0,0,0.06);
  &.no-messages {
    height: 100%;
    justify-self: center;
  }
}

.messages.scrolled {
  box-shadow: inset 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.thinking {
  display: flex;
  gap: 4px;
  margin: 8px 0;
}

.dot {
  width: 6px;
  height: 6px;
  background: #999;
  border-radius: 50%;
  animation: blink 1.2s infinite ease-in-out;
}

.dot:nth-child(2) { animation-delay: 0.2s; }
.dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes blink {
  0%, 80%, 100% { opacity: 0.2; }
  40% { opacity: 1; }
}
</style>
